import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import {
  usePaneLocation,
  usePaneParams,
  usePaneRouterContextSafe,
} from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import { entityWithEnrichedOpenTabUrl } from "@/panes/shared/entityWithEnrichedOpenTabUrl";
import {
  IntegrationIcon,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { format } from "date-fns";
import { Clock, ExternalLink, MapPin, Users, Video, X } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCalendarEventFetch } from "./hooks/useCalendarEventFetch";
import { useGoogleCalendarConnection } from "./hooks/useGoogleCalendarConnection";

function formatEventTime(start?: string, end?: string): string | undefined {
  if (!start) return undefined;
  const s = new Date(start);
  if (Number.isNaN(s.getTime())) return undefined;
  if (end) {
    const e = new Date(end);
    if (!Number.isNaN(e.getTime())) {
      const sameDay = s.toDateString() === e.toDateString();
      return sameDay
        ? `${format(s, "PPP")} · ${format(s, "p")} – ${format(e, "p")}`
        : `${format(s, "PPp")} – ${format(e, "PPp")}`;
    }
  }
  return format(s, "PPpp");
}

function attendeeNames(attendees: unknown): string[] {
  if (!Array.isArray(attendees)) return [];
  return attendees
    .map((a) => {
      const r = (a ?? {}) as { display_name?: string; email?: string };
      return r.display_name || r.email || "";
    })
    .filter(Boolean);
}

/**
 * Google Calendar event detail. Renders the generic entity immediately and
 * enriches it with description + attendees fetched by id via
 * `google-calendar-event-get` (calendar defaults to "primary"; non-primary
 * events keep the generic view).
 */
export function CalendarEventDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const paneLocation = usePaneLocation("rightPane");
  const mainLocation = useLocation();
  const { eventId } = usePaneParams(ROUTES.GOOGLE_CALENDAR.EVENT_DETAIL);

  const entity = paneLocation?.state?.entity ?? mainLocation.state?.entity;
  const data = (entity?.data ?? {}) as Record<string, unknown>;
  const id = (data.id as string | undefined) ?? eventId ?? "";

  const { connectionId } = useGoogleCalendarConnection();
  const { data: event, isLoading } = useCalendarEventFetch({
    integrationConnectionId: connectionId,
    eventId: id,
    enabled: !!connectionId && !!id,
  });

  const title =
    (event?.summary as string | undefined) ??
    (data.title as string | undefined) ??
    "Untitled event";
  const when =
    formatEventTime(
      (event?.start_date_time as string | undefined) ??
        (event?.start_date as string | undefined),
      (event?.end_date_time as string | undefined) ??
        (event?.end_date as string | undefined),
    ) ?? formatEventTime(data.timestamp as string | undefined);
  const location =
    (event?.location as string | undefined) ??
    (data.subtitle as string | undefined);
  const description = event?.description as string | undefined;
  const names = attendeeNames(event?.attendees);
  const url =
    (data.url as string | undefined) ??
    (event?.html_link as string | undefined) ??
    (event?.web_link as string | undefined);
  // Video-conference join link (Meet/Zoom/etc.). Comes from the enrich fetch;
  // a typed-placed event may also carry it on the generic data.
  const meetingLink =
    (event?.meeting_link as string | undefined) ??
    (data.meeting_link as string | undefined);

  const entityForHeaderActions = useMemo(
    () =>
      entity
        ? entityWithEnrichedOpenTabUrl(entity, {
            url: data.url as string | undefined,
            html_link: event?.html_link as string | undefined,
            web_link: event?.web_link as string | undefined,
          })
        : undefined,
    [entity, data.url, event?.html_link, event?.web_link],
  );

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  const handleOpenExternal = useCallback(() => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [url]);

  const handleJoinMeeting = useCallback(() => {
    if (meetingLink) window.open(meetingLink, "_blank", "noopener,noreferrer");
  }, [meetingLink]);

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />

          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="google_calendar"
              name="Google Calendar"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {title}
            </span>
          </div>

          <TooltipProvider>
            {entityForHeaderActions && (
              <EntityPaneHeaderObjectActions
                entity={entityForHeaderActions}
                name={title}
              />
            )}

            <PaneExpandButton />

            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent className="!p-0">
        <div className="flex flex-col gap-4 p-4">
          <div>
            <h2 className="text-base font-semibold text-foreground leading-snug">
              {title}
            </h2>
            <div className="mt-1.5 flex flex-col gap-1 text-sm text-muted-foreground">
              {when && (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  {when}
                </span>
              )}
              {location && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {location}
                </span>
              )}
            </div>
          </div>

          {meetingLink && (
            <div>
              <Button size="sm" className="gap-1.5" onClick={handleJoinMeeting}>
                <Video className="h-3.5 w-3.5" />
                Join meeting
              </Button>
            </div>
          )}

          {names.length > 0 && (
            <div>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                Guests
              </p>
              <p className="text-sm text-foreground">{names.join(", ")}</p>
            </div>
          )}

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Description
            </p>
            {isLoading && !event ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            ) : description ? (
              <p className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                {description}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No description.</p>
            )}
          </div>

          {url && (
            <div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleOpenExternal}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in Google Calendar
              </Button>
            </div>
          )}
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
